import React from 'react';
import SearchBarComp from 'src/components/SearchBar/SearchBar';
import debounce from '@mui/material/utils/debounce';
import FormGroup from '@mui/material/FormGroup';
import { StyledText, StyledTextSub } from 'src/components/StyledTypography';
import { useTranslate } from '@im/react-localization';
import { EndUserInfo } from '../../hooks/end-user-hook';
import Stack from '@mui/material/Stack';
import { UseMutateFunction } from 'react-query';
import { EndUserSearchResponse, getERPType } from '../../hooks/exceptionDeal';
import { useSearchParams } from 'react-router-dom';

const DEBOUCE_DELAY = 500;

function SearchExistingEndUserForm(
  props: Readonly<{
    data: EndUserSearchResponse[] | undefined;
    disableEndUser: boolean;
    endUserTextValue: string;
    handleEndUserOptionSelected: (option: any) => void;
    mutate: UseMutateFunction<
      EndUserSearchResponse[],
      unknown,
      {
        acopEndUserId?: number;
        endUserId?: number;
        endUserName?: string;
        erpType?: string;
        resellerEndUserId?: number;
        searchType?: string;
      },
      unknown
    >;
    prefilledEndUser: string | undefined;
    productType?: string;
    setEndUserTextValue: (value: string) => void;
  }>
) {
  const {
    data,
    disableEndUser,
    endUserTextValue,
    handleEndUserOptionSelected,
    mutate,
    prefilledEndUser,
    productType,
    setEndUserTextValue,
  } = props;
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const businessType = searchParams.get('businessType');
  const getEndUserOptionLabel = (option: EndUserInfo) => {
    if (typeof option === 'string') {
      return option;
    }
    const endUserIdentifier =
      businessType === 'Subscription'
        ? option.endUserId
        : option.address[0]?.acopEndUserId;
    return `${option.endUserName} ~ ${endUserIdentifier}`;
  };

  const renderEndUserOption = (option: EndUserInfo) => {
    const address = option.address[0];
    const fullAddress = `${address.addressLine1}, ${address.city}, ${address.state} ${address.postalCode}, ${address.countryCode}`;
    return (
      <Stack width={'100%'}>
        <Stack
          alignItems={'center'}
          direction={'row'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <StyledText>{option.endUserName}</StyledText>
          <StyledText>{option.address[0]?.acopEndUserId}</StyledText>
        </Stack>
        <Stack width={'100%'}>
          <StyledTextSub>{fullAddress}</StyledTextSub>
        </Stack>
        {(businessType === 'Subscription' || productType === 'Hybrid') && (
          <Stack width={'100%'}>
            <StyledTextSub>
              {translate('VendorProgram.CmpEnduserIdNumber')}:
              {option.address[0]?.cmpEndCustomerId}
            </StyledTextSub>
          </Stack>
        )}
      </Stack>
    );
  };

  const getOptions = () => {
    const newOptions: EndUserInfo[] = [];
    if (businessType === 'Subscription' || productType === 'Hybrid') {
      return getCMPEnduserOptions();
    }
    data?.forEach((record) => {
      record.address
        .filter((ad) => ad.acopEndUserId != null)
        .forEach((address) => {
          newOptions.push({
            address: [address],
            endUserId: record.endUserId ?? 0,
            endUserName: record.endUserName,
          });
        });
    });
    return newOptions;
  };

  const getCMPEnduserOptions = () => {
    const newOptions: EndUserInfo[] = [];
    data?.forEach((record) => {
      if (
        record.endUserId != null &&
        record.address &&
        Object.keys(record.address).length > 0
      ) {
        newOptions.push({
          address: [record.address[0]],
          endUserId: record.endUserId,
          // cmpEndCustomerId: Number(record.address[0].cmpEndCustomerId),
          endUserName: record.endUserName,
        });
      }
    });
    return newOptions;
  };

  const handleOnFocus = () => {
    if (
      prefilledEndUser &&
      prefilledEndUser.length > 1 &&
      !prefilledEndUser.includes('~')
    ) {
      mutate({
        endUserName: prefilledEndUser,
        erpType: businessType === 'Subscription' ? 'C' : 'I',
      });
    }
  };
  return (
    <>
      <FormGroup>
        <StyledText>{translate('VendorDeals.EndUserName')}</StyledText>
        <SearchBarComp
          autoCompleteDataTestId="gds-vp-ed-enduser-name-searchbar"
          defaultValue={prefilledEndUser}
          disabled={disableEndUser}
          exception={true}
          getOptionLabel={getEndUserOptionLabel}
          inputValue={endUserTextValue}
          onFocus={handleOnFocus}
          onInputChange={debounce((value: string) => {
            setEndUserTextValue(value);
            if (value.length > 1 && !value.includes('~'))
              mutate({
                endUserName: value,
                erpType: getERPType(
                  businessType === 'Subscription',
                  productType === 'Hybrid'
                ),
              });
          }, DEBOUCE_DELAY)}
          onOptionSelected={handleEndUserOptionSelected}
          options={getOptions() ?? []}
          placeHolder={translate('VendorDeals.EndUserSearchByNamePlaceholder')}
          renderOption={renderEndUserOption}
          textFieldDataTestId="gds-vp-ed-enduser-name-textfield"
        />
      </FormGroup>
      <FormGroup>
        <StyledText>
          {businessType === 'Subscription'
            ? translate('VendorDeals.CmpEndUserSearchByNoLabel')
            : translate('VendorDeals.EndUserSearchByNoLabel')}
        </StyledText>
        <SearchBarComp
          autoCompleteDataTestId="gds-vp-ed-ACOPenduser-number-searchbar"
          disabled={disableEndUser}
          exception={true}
          getOptionLabel={getEndUserOptionLabel}
          onInputChange={debounce((value: string) => {
            if (value.length > 1 && !value.includes('~'))
              mutate({
                ...(businessType === 'Subscription'
                  ? {
                      endUserId: Number(value),
                      erpType: getERPType(
                        businessType === 'Subscription',
                        productType === 'Hybrid'
                      ),
                    }
                  : {
                      acopEndUserId: Number(value),
                      erpType: getERPType(
                        businessType === 'Subscription',
                        productType === 'Hybrid'
                      ),
                    }),
                searchType: 'exact',
              });
          }, DEBOUCE_DELAY)}
          onOptionSelected={handleEndUserOptionSelected}
          options={getOptions() ?? []}
          placeHolder={
            businessType === 'Subscription'
              ? translate('VendorDeals.CmpEndUserSearchByNoPlaceholder')
              : translate('VendorDeals.EndUserSearchByNoPlaceholder')
          }
          renderOption={renderEndUserOption}
          textFieldDataTestId="gds-vp-ed-ACOPenduser-number-textfield"
        />
      </FormGroup>
      {(businessType === 'Subscription' || productType === 'Hybrid') && (
        <FormGroup>
          <StyledText>
            {translate('VendorDeals.EndUserSearchByCMPID')}
          </StyledText>
          <SearchBarComp
            autoCompleteDataTestId="gds-vp-ed-CMP-enduser-number-searchbar"
            disabled={disableEndUser}
            exception={true}
            getOptionLabel={getEndUserOptionLabel}
            onInputChange={debounce((value: string) => {
              if (value.length > 1 && !value.includes('~'))
                mutate({
                  erpType: getERPType(
                    businessType === 'Subscription',
                    productType === 'Hybrid'
                  ),
                  resellerEndUserId: Number(value),
                  searchType: 'exact',
                });
            }, DEBOUCE_DELAY)}
            onOptionSelected={handleEndUserOptionSelected}
            options={getOptions() ?? []}
            placeHolder={translate(
              'VendorDeals.EndUserSearchByCMPIDPlaceholder'
            )}
            renderOption={renderEndUserOption}
            textFieldDataTestId="gds-vp-ed-CMP-enduser-number-textfield"
          />
        </FormGroup>
      )}
    </>
  );
}

export default SearchExistingEndUserForm;
